import type { TenantResponse, UserResponse } from '@/api/responses'

/**
 * Decisoes de navegacao dos guards de rota, como valores puros (sem depender do router).
 * Mantido separado de `app/routeGuards.ts` para que a regra de negocio (quem pode ver o que,
 * e para onde vai quem nao pode) seja testavel sem renderizar rotas nem mockar o router.
 */
export type RouteGuardDecision =
  | { readonly kind: 'allow' }
  | { readonly kind: 'redirect'; readonly to: '/login' }
  | { readonly kind: 'redirect'; readonly to: '/onboarding' }
  | {
      readonly kind: 'redirect'
      readonly to: '/app/$tenantSlug/dashboard'
      readonly params: { readonly tenantSlug: string }
    }

/**
 * `tenant.status` so vira `Active` quando a saga de provisioning termina (`MarkReadyStep` — WP-07)
 * e o app shell assume que agente/canal/knowledge default ja existem. Um tenant `Provisioning`
 * (ou qualquer outro status ainda nao operacional) pertence a wizard de onboarding, nao ao
 * dashboard: e assim que "fechar e reabrir o browser" retoma no passo certo (a wizard deriva o
 * proprio passo do mesmo `tenant.status`, em `features/onboarding/wizardSteps.ts`).
 */
function isOperational(tenant: TenantResponse): boolean {
  return tenant.status === 'Active'
}

/**
 * A PRIMEIRA organizacao OPERACIONAL (Active) do usuario — nao apenas a primeira da lista. Sem isto, uma org
 * nao-operacional (ex.: outra ainda em provisioning, ou uma recem-excluida ainda em cache) que ordene primeiro
 * mandaria o usuario para o onboarding mesmo existindo uma org Ativa. Undefined quando nenhuma e operacional.
 */
function firstOperationalTenant(tenants: readonly TenantResponse[]): TenantResponse | undefined {
  return tenants.find(isOperational)
}

/** Acesso a /login e /signup: usuario ja autenticado e redirecionado para longe do formulario. */
export function decideAuthPageAccess(
  user: UserResponse | null,
  tenants: readonly TenantResponse[],
): RouteGuardDecision {
  if (user === null) {
    return { kind: 'allow' }
  }

  const tenant = firstOperationalTenant(tenants)

  if (tenant === undefined) {
    return { kind: 'redirect', to: '/onboarding' }
  }

  return { kind: 'redirect', to: '/app/$tenantSlug/dashboard', params: { tenantSlug: tenant.slug } }
}

/**
 * Acesso a /onboarding: exige sessao; quem ja tem organizacao operacional vai direto para o
 * dashboard dela. Organizacao ainda em provisioning permanece na wizard (retomada).
 */
export function decideOnboardingAccess(
  user: UserResponse | null,
  tenants: readonly TenantResponse[],
): RouteGuardDecision {
  if (user === null) {
    return { kind: 'redirect', to: '/login' }
  }

  const tenant = firstOperationalTenant(tenants)

  if (tenant !== undefined) {
    return {
      kind: 'redirect',
      to: '/app/$tenantSlug/dashboard',
      params: { tenantSlug: tenant.slug },
    }
  }

  return { kind: 'allow' }
}

/**
 * Acesso a /app/:tenantSlug/*: exige sessao e ao menos uma organizacao operacional (Active); uma
 * organizacao ainda em provisioning volta para a wizard em vez de mostrar um dashboard pela
 * metade. Se o slug da URL nao corresponder a uma organizacao operacional do usuario, cai na
 * primeira organizacao operacional valida em vez de mostrar um shell quebrado.
 */
export function decideAppShellAccess(
  user: UserResponse | null,
  tenants: readonly TenantResponse[],
  tenantSlug: string,
): RouteGuardDecision {
  if (user === null) {
    return { kind: 'redirect', to: '/login' }
  }

  const tenant = firstOperationalTenant(tenants)

  if (tenant === undefined) {
    return { kind: 'redirect', to: '/onboarding' }
  }

  const matches = tenants.some(
    (candidate) => candidate.slug === tenantSlug && isOperational(candidate),
  )

  if (!matches) {
    return {
      kind: 'redirect',
      to: '/app/$tenantSlug/dashboard',
      params: { tenantSlug: tenant.slug },
    }
  }

  return { kind: 'allow' }
}
